"use strict";
/* globals module */
/**
 * @module articleClient
 * @description  Articles Client provides an easy way to consume  Articles REST API end-points. In order to obtain needed routes `articleClient` uses `articleRoute`.
 */
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
var inversify_1 = require("inversify");
;
var common_1 = require("../../common");
var httpApi_1 = require("../../httpApi");
var _1 = require("./");
var ArticleClient = /** @class */ (function () {
    function ArticleClient(articleInstanceCommentsClient, articleInstanceFilesClient, articleInstanceRatingsClient, articleInstanceTagsClient, articleInstanceSubscriptionsClient, articleRoute, articleACLClient, apiClient) {
        this.articleInstanceCommentsClient = articleInstanceCommentsClient;
        this.articleInstanceFilesClient = articleInstanceFilesClient;
        this.articleInstanceRatingsClient = articleInstanceRatingsClient;
        this.articleInstanceTagsClient = articleInstanceTagsClient;
        this.articleInstanceSubscriptionsClient = articleInstanceSubscriptionsClient;
        this.articleRoute = articleRoute;
        this.articleACLClient = articleACLClient;
        this.apiClient = apiClient;
        this.utility = new common_1.Utility();
        this.articleUtility = new _1.BaasicArticleUtility();
        this.statuses = _1.ArticleStatus;
        this.commentStatuses = _1.CommentStatus;
    }
    Object.defineProperty(ArticleClient.prototype, "routeDefinition", {
        /**
         * Provides direct access to `articleRoute`.
         * @method
         * @example articleClient.routeDefinition.get();
         **/
        get: function () {
            return this.articleRoute;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ArticleClient.prototype, "comments", {
        get: function () {
            return this.articleInstanceCommentsClient;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ArticleClient.prototype, "files", {
        get: function () {
            return this.articleInstanceFilesClient;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ArticleClient.prototype, "tags", {
        get: function () {
            return this.articleInstanceTagsClient;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ArticleClient.prototype, "ratings", {
        get: function () {
            return this.articleInstanceRatingsClient;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ArticleClient.prototype, "subscriptions", {
        get: function () {
            return this.articleInstanceSubscriptionsClient;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ArticleClient.prototype, "acl", {
        get: function () {
            return this.articleACLClient;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Returns a promise that is resolved once the find action has been performed. Success response returns a list of article resources matching the given criteria.
     * @method
     * @param options A promise that is resolved once the find action has been performed.
     * @returns A promise that is resolved once the find action has been performed.
     * @example articleClient.find({
                    pageNumber : 1,
                    pageSize : 10,
                    orderBy : '<field>',
                    orderDirection : '<asc|desc>',
                    search : '<search-phrase>'
                })
                .then(function (collection) {
                    // perform success action here
                },
                 function (response, status, headers, config) {
                     // perform error handling here
                });
     **/
    ArticleClient.prototype.find = function (options) {
        return this.apiClient.get(this.routeDefinition.find(options));
    };
    /**
     * Returns a promise that is resolved once the get action has been performed. Success response returns a single article resource.
     * @method
     * @param id Article slug or id which uniquely identifies article resource that needs to be retrieved.
     * @param options Options object that contains embed items.
     * @returns a promise that is resolved once the get action has been performed.
     * @example articleClient.get('<article-id>')
                    .then(function (data) {
                        // perform success action here
                    },
                     function (response, status, headers, config) {
                         // perform error handling here
                    });
     **/
    ArticleClient.prototype.get = function (id, options) {
        return this.apiClient.get(this.articleRoute.get(id, options));
    };
    /**
     * Returns a promise that is resolved once the create article action has been performed, this action creates a new article resource.
     * @method
     * @param data An article object that needs to be inserted into the system.
     * @returns a promise that is resolved once the create article action has been performed.
     * @example articleClient.create({
                    publishDate : new Date(),
                    title : '<title>',
                    content : '<content>',
                    slug : '',
                    status : baasicArticleService.statuses.draft,
                    $tags : ['<tag1>', '<tag2>']
                })
                .then(function (data) {
                    // perform success action here
                },
                 function (response, status, headers, config) {
                     // perform error handling here
                });
     **/
    ArticleClient.prototype.create = function (data) {
        return this.apiClient.post(this.routeDefinition.create(), this.routeDefinition.createParams(data));
    };
    /**
     * Returns a promise that is resolved once the update article action has been performed; this action updates an article resource. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `articleRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.updateParams(article);
     * let uri = params['model'].links('put').href;
     * ```
     * @method
     * @param data An article object that needs to be updated into the system.
     * @returns A promise that is resolved once the update article action has been performed.
     * @example // article is a resource previously fetched using get action.
                    article.title = '<title>';
                    articleClient.update(article)
                        .then(function (data) {
                            // perform success action here
                        },
                         function (response, status, headers, config) {
                             // perform error handling here
                        });
     **/
    ArticleClient.prototype.update = function (data) {
        return this.apiClient.put(this.routeDefinition.update(data), this.routeDefinition.updateParams(data));
    };
    /**
     * Returns a promise that is resolved once the saveDraft article action has been performed. This action saves an article with "draft" status. If an article does not exist it will create a new article resource otherwise it will update an existing article resource.
     * @method
     * @param data An article object that needs to be inserted into the system.
     * @returns A promise that is resolved once the saveDraft article action has been performed.
     * @example // article is a resource previously fetched using get action.
                        articleClient.saveDraft(article)
                            .then(function (data) {
                                // perform success action here
                            },
                             function (response, status, headers, config) {
                                 // perform error handling here
                            });
     **/
    ArticleClient.prototype.saveDraft = function (data) {
        if (this.utility.isUndefined(data.id)) {
            // Create new draft
            return this.create(data);
        }
        // Update draft
        return this.update(data);
    };
    /**
     * Returns a promise that is resolved once the remove article action has been performed. If the action is successfully completed, the article resource will be permanently removed from the system. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `articleRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.removeParams(article);
     * let uri = params['model'].links('delete').href;
     * ```
     * @method
     * @param data An article object that needs to be removed from the system.
     * @returns A promise that is resolved once the remove article action has been performed.
     * @example // article is a resource previously fetched using get action.
                    articleClient.remove(article)
                        .then(function (data) {
                            // perform success action here
                        },
                         function (response, status, headers, config) {
                             // perform error handling here
                        });
     **/
    ArticleClient.prototype.remove = function (data) {
        return this.apiClient.delete(this.routeDefinition.delete(data));
    };
    /**
     * Returns a promise that is resolved once the archive article action has been performed. This action sets the status of an article from "published" to "archive". This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `articleRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.updateParams(article);
     * let uri = params['model'].links('archive').href;
     * ```
     * @method
     * @param data An article object.
     * @param options Notification options.
     * @returns A promise that is resolved once the archive article action has been performed.
     * @example // article is a resource previously fetched using get action.
                    articleClient.archive(article, articleOptions)
                        .then(function (data) {
                            // perform success action here
                        },
                         function (response, status, headers, config) {
                             // perform error handling here
                        });
     **/
    ArticleClient.prototype.archive = function (data, options) {
        return this.apiClient.put(this.routeDefinition.archive(data), this.routeDefinition.updateParams(options));
    };
    /**
     * Returns a promise that is resolved once the restore article action has been performed. This action sets the status of an article from "archive" to "published". This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `baasicArticleRouteService` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.updateParams(article);
     * let uri = params['model'].links('restore').href;
     * ```
     * @method
     * @param data Article object.
     * @returns A promise that is resolved once the restore article action has been performed.
     * @example // article is a resource previously fetched using get action.
                        articleClient.restore(article)
                            .then(function (data) {
                                // perform success action here
                            },
                             function (response, status, headers, config) {
                                 // perform error handling here
                            });
     **/
    ArticleClient.prototype.restore = function (data) {
        return this.apiClient.put(this.routeDefinition.restore(data), this.routeDefinition.updateParams(data));
    };
    /**
     * Returns a promise that is resolved once the unpublish article action has been performed. This action sets the status of an article from "published" to "draft". This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `articleRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.updateParams(article);
     * let uri = params['model'].links('unpublish').href;
     * ```
     * @method
     * @param data An article object.
     * @returns A promise that is resolved once the unpublish article action has been performed.
     * @example 	// article is a resource previously fetched using get action.
                        articleClient.unpublish(article)
                            .then(function (data) {
                                // perform success action here
                            },
                             function (response, status, headers, config) {
                                 // perform error handling here
                            });
     **/
    ArticleClient.prototype.unpublish = function (data) {
        return this.apiClient.put(this.routeDefinition.unpublish(data), this.routeDefinition.updateParams(data));
    };
    /**
     * Returns a promise that is resolved once the publish article action has been performed. This action sets the status of an article from "draft" to "published".
     * @method
     * @param data An article object.
     * @param articleOptions Notification options.
     * @returns A promise that is resolved once the unpublish article action has been performed.
     * @example articleClient.publish(article, articleOptions)
                    .then(function (data) {
                        // perform success action here
                    },
                     function (response, status, headers, config) {
                         // perform error handling here
                    });
     **/
    ArticleClient.prototype.publish = function (data, articleOptions) {
        return this.apiClient.put(this.routeDefinition.publish(data), this.routeDefinition.updateParams(articleOptions));
    };
    /**
     * Returns a promise that is resolved once the purge articles action has been performed. Please note that all article resources will be deleted from the system once the action is successfully completed and therefore it can only be executed by user assigned to account owner role.
     * @method
     * @example articleClient.purge({})
                    .then(function (data) {
                        // perform success action here
                    },
                     function (response, status, headers, config) {
                         // perform error handling here
                    });
     **/
    ArticleClient.prototype.purge = function (options) {
        return this.apiClient.delete(this.routeDefinition.purge(options));
    };
    var _a, _b, _c, _d, _e, _f, _g, _h;
    ArticleClient = tslib_1.__decorate([
        inversify_1.injectable(),
        tslib_1.__param(0, inversify_1.inject(_1.TYPES.ArticleInstanceCommentsClient)),
        tslib_1.__param(1, inversify_1.inject(_1.TYPES.ArticleInstanceFilesClient)),
        tslib_1.__param(2, inversify_1.inject(_1.TYPES.ArticleInstanceRatingsClient)),
        tslib_1.__param(3, inversify_1.inject(_1.TYPES.ArticleInstanceTagsClient)),
        tslib_1.__param(4, inversify_1.inject(_1.TYPES.ArticleInstanceSubscriptionsClient)),
        tslib_1.__param(5, inversify_1.inject(_1.TYPES.ArticleRoute)),
        tslib_1.__param(6, inversify_1.inject(_1.TYPES.ArticleACLClient)),
        tslib_1.__param(7, inversify_1.inject(httpApi_1.httpTYPES.ApiClient)),
        tslib_1.__metadata("design:paramtypes", [typeof (_a = typeof _1.ArticleInstanceCommentsClient !== "undefined" && _1.ArticleInstanceCommentsClient) === "function" ? _a : Object, typeof (_b = typeof _1.ArticleInstanceFilesClient !== "undefined" && _1.ArticleInstanceFilesClient) === "function" ? _b : Object, typeof (_c = typeof _1.ArticleInstanceRatingsClient !== "undefined" && _1.ArticleInstanceRatingsClient) === "function" ? _c : Object, typeof (_d = typeof _1.ArticleInstanceTagsClient !== "undefined" && _1.ArticleInstanceTagsClient) === "function" ? _d : Object, typeof (_e = typeof _1.ArticleInstanceSubscriptionsClient !== "undefined" && _1.ArticleInstanceSubscriptionsClient) === "function" ? _e : Object, typeof (_f = typeof _1.ArticleRoute !== "undefined" && _1.ArticleRoute) === "function" ? _f : Object, typeof (_g = typeof _1.ArticleACLClient !== "undefined" && _1.ArticleACLClient) === "function" ? _g : Object, typeof (_h = typeof httpApi_1.ApiClient !== "undefined" && httpApi_1.ApiClient) === "function" ? _h : Object])
    ], ArticleClient);
    return ArticleClient;
}());
exports.ArticleClient = ArticleClient;
/**
 * @overview
 ***Notes:**
 - Refer to the [REST API documentation](https://github.com/Baasic/baasic-rest-api/wiki) for detailed information about available Baasic REST API end-points.
 - All end-point objects are transformed by the associated route service.
 */ 
